import { useState, type ReactNode } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useNavigate, useLocation, Navigate } from 'react-router-dom'
import { FirebaseError } from 'firebase/app'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { useSession } from '@/features/auth/session'
import { ApiError } from '@/lib/api-client'
import logoUrl from '@/assets/logo.png'

const loginSchema = z.object({
  email: z.string().email('Ingresa un email válido'),
  password: z.string().min(6, 'Mínimo 6 caracteres'),
})

type LoginFormValues = z.infer<typeof loginSchema>

export function LoginPage(): ReactNode {
  const { signIn, status } = useSession()
  const navigate = useNavigate()
  const location = useLocation()
  const [submitError, setSubmitError] = useState<string | null>(null)

  const form = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: '', password: '' },
  })

  if (status === 'authenticated') {
    const fromState = location.state as { from?: string } | null
    const from = fromState?.from ?? '/dashboard'
    return <Navigate to={from} replace />
  }

  const onSubmit = async (values: LoginFormValues): Promise<void> => {
    setSubmitError(null)
    try {
      await signIn(values.email, values.password)
      toast.success('Sesión iniciada')
      navigate('/dashboard', { replace: true })
    } catch (err) {
      const message = resolveErrorMessage(err)
      setSubmitError(message)
      toast.error(message)
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-muted/30 p-4">
      <Card className="w-full max-w-sm">
        <CardHeader className="items-center text-center">
          <img
            src={logoUrl}
            alt="Escuela de Sommeliers"
            className="mb-2 h-24 w-24"
          />
          <CardTitle>Escuela de Sommeliers</CardTitle>
          <CardDescription>Ingresa con tu cuenta de administrador.</CardDescription>
        </CardHeader>
        <CardContent>
          <form
            noValidate
            className="flex flex-col gap-4"
            onSubmit={form.handleSubmit(onSubmit)}
          >
            <div className="flex flex-col gap-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                autoFocus
                disabled={form.formState.isSubmitting}
                {...form.register('email')}
              />
              {form.formState.errors.email ? (
                <p className="text-xs text-destructive">
                  {form.formState.errors.email.message}
                </p>
              ) : null}
            </div>
            <div className="flex flex-col gap-2">
              <Label htmlFor="password">Contrasena</Label>
              <Input
                id="password"
                type="password"
                autoComplete="current-password"
                disabled={form.formState.isSubmitting}
                {...form.register('password')}
              />
              {form.formState.errors.password ? (
                <p className="text-xs text-destructive">
                  {form.formState.errors.password.message}
                </p>
              ) : null}
            </div>
            {submitError ? (
              <Alert variant="destructive">
                <AlertDescription>{submitError}</AlertDescription>
              </Alert>
            ) : null}
            <Button type="submit" disabled={form.formState.isSubmitting}>
              {form.formState.isSubmitting ? 'Ingresando...' : 'Ingresar'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  )
}

function resolveErrorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.code === 'NETWORK_ERROR') {
      return 'No pudimos conectarnos al servidor. Verifica tu conexión o intenta más tarde.'
    }
    if (err.code === 'NOT_ADMIN') {
      return 'Esta cuenta no tiene permisos de administrador.'
    }
    return err.message || 'No pudimos iniciar sesión.'
  }
  if (err instanceof FirebaseError) {
    switch (err.code) {
      case 'auth/invalid-credential':
      case 'auth/invalid-email':
      case 'auth/wrong-password':
      case 'auth/user-not-found':
        return 'Email o contraseña incorrectos.'
      case 'auth/too-many-requests':
        return 'Demasiados intentos. Intenta nuevamente en unos minutos.'
      case 'auth/network-request-failed':
        return 'Problema de conexión. Reintenta.'
      default:
        return 'No pudimos iniciar sesión. Intenta nuevamente.'
    }
  }
  return 'No pudimos iniciar sesión. Intenta nuevamente.'
}
